// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// Feature 083: the recursive step-flow grammar, client side — the Event
// Storming core. One line per step: `[id] label`, nesting via `:: in
// [parent]`, trigger via `? on|at|by <text>` (event / timer / manual;
// absent = follows). Mirrors functions/_stepFlow.js; structure rides
// clauses, never indentation.
//
// toStepParsedFlow adapts the step tree to the ParsedFlow shape FlowCanvas
// already renders. The layout ONLY places statements that carry an actor,
// and never places `event`-type statements (those embed in a producer card)
// — so every step becomes an `action` card in ONE neutral lane, sequential
// `from` triggers linking them, the trigger text carried on the card label.
// Swimlanes MEAN bounded contexts / actors / systems in this canvas, and the
// step core deliberately has none of those yet — real lanes arrive with the
// domain overlay slice, never as a rendering trick (owner direction,
// 2026-08-25: a lane per step/process misuses the lane vocabulary).

import type { ParsedFlow, Statement, Trigger } from './flowModel';

export type StepTriggerKind = 'follows' | 'event' | 'timer' | 'manual';

export interface StepNode {
  id: number;
  label: string;
  trigger: { kind: StepTriggerKind; text: string };
  alternative?: { groupId: string; label: string };
  steps: StepNode[];
}

export interface StepFlowResult {
  steps: StepNode[];
  errors: string[];
}

export interface SharedProcessSpineStep {
  stepId: string;
  label: string;
  order: number;
  parentStepId: string | null;
  trigger?: { kind: StepTriggerKind; text: string };
  alternative?: { groupId: string; label: string };
  latest?: boolean;
  needsLook?: boolean;
  provenance: { source: 'event-storming-flow'; statementId: number };
}

const LINE_RE = /^\[(\d+)\]\s*(.*)$/;
const TRIGGER_KINDS: Record<string, StepTriggerKind> = { on: 'event', at: 'timer', by: 'manual' };

export function parseStepFlow(flowText: string): StepFlowResult {
  const errors: string[] = [];
  const nodes = new Map<number, StepNode & { parentId: number | null }>();
  const order: number[] = [];
  for (const rawLine of (flowText || '').split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(LINE_RE);
    if (!match) { errors.push(`unparseable line: ${line.slice(0, 60)}`); continue; }
    const id = parseInt(match[1], 10);
    let rest = match[2];
    let alternative: StepNode['alternative'];
    const alternativeMatch = rest.match(/::\s*alternative\s+(\S+)\s+(\S+)/);
    if (alternativeMatch) {
      try { alternative = { groupId: decodeURIComponent(alternativeMatch[1]), label: decodeURIComponent(alternativeMatch[2]) }; }
      catch { errors.push(`invalid alternative encoding on [${id}]`); }
      rest = rest.replace(alternativeMatch[0], '').trim();
    }

    let trigger: StepNode['trigger'] = { kind: 'follows', text: '' };
    const triggerMatch = rest.match(/\?\s*(on|at|by)\s+(.+)$/);
    if (triggerMatch) {
      trigger = { kind: TRIGGER_KINDS[triggerMatch[1]], text: triggerMatch[2].trim() };
      rest = rest.slice(0, triggerMatch.index).trim();
    }

    let parentId: number | null = null;
    const parentMatch = rest.match(/::\s*in\s*\[(\d+)\]/);
    if (parentMatch) {
      parentId = parseInt(parentMatch[1], 10);
      rest = (rest.slice(0, parentMatch.index)
        + rest.slice((parentMatch.index || 0) + parentMatch[0].length)).trim();
    }

    if (nodes.has(id)) { errors.push(`duplicate id [${id}] — later line ignored`); continue; }
    nodes.set(id, { id, label: rest.trim(), trigger, parentId, steps: [], ...(alternative ? { alternative } : {}) });
    order.push(id);
  }

  // Break parent cycles by degrading the cycling node to a root.
  for (const id of order) {
    const seen = new Set([id]);
    let current = nodes.get(id);
    while (current && current.parentId !== null && nodes.has(current.parentId)) {
      if (seen.has(current.parentId)) {
        errors.push(`parent cycle at [${id}] — treated as a root`);
        nodes.get(id)!.parentId = null;
        break;
      }
      seen.add(current.parentId);
      current = nodes.get(current.parentId);
    }
  }

  const roots: StepNode[] = [];
  for (const id of order) {
    const node = nodes.get(id)!;
    if (node.parentId === null) {
      roots.push(node);
    } else if (nodes.has(node.parentId)) {
      nodes.get(node.parentId)!.steps.push(node);
    } else {
      errors.push(`[${node.id}] names unknown parent [${node.parentId}] — treated as a root`);
      roots.push(node);
    }
  }
  const strip = (node: StepNode & { parentId?: number | null }): StepNode => ({
    id: node.id, label: node.label, trigger: node.trigger, steps: node.steps.map(strip), ...(node.alternative ? { alternative: node.alternative } : {}),
  });
  return { steps: roots.map(strip), errors };
}

// Compatibility projection for Feature 084. Numeric Event Storming ids remain
// presentation/source ids; the engagement-owned identity is namespaced by the
// stable spine id and is therefore deterministic across reload and migration.
export function toProcessSpineCapture(spineId: string, flowText: string): {
  steps: SharedProcessSpineStep[];
  needsLook: boolean;
} {
  const parsed = parseStepFlow(flowText);
  const output: SharedProcessSpineStep[] = [];
  const stableId = (id: number) => `${spineId}:event-storming:${id}`;

  const visitSiblings = (siblings: StepNode[], parentStepId: string | null) => {
    siblings.forEach((node, order) => {
      const stepId = stableId(node.id);
      output.push({
        stepId,
        label: node.label,
        order,
        parentStepId,
        ...(node.trigger.kind !== 'follows' ? { trigger: node.trigger } : {}),
        ...(node.alternative ? { alternative: node.alternative } : {}),
        provenance: { source: 'event-storming-flow', statementId: node.id },
      });
      visitSiblings(node.steps, stepId);
    });
  };
  visitSiblings(parsed.steps, null);
  if (output.length > 0) output[output.length - 1] = { ...output[output.length - 1], latest: true };
  if (parsed.errors.length > 0 && output.length > 0) {
    output[output.length - 1] = { ...output[output.length - 1], needsLook: true };
  }
  return { steps: output, needsLook: parsed.errors.length > 0 };
}

const TRIGGER_PREFIX: Record<StepTriggerKind, string> = {
  follows: '', event: '? on', timer: '? at', manual: '? by',
};

function cardName(step: StepNode): string {
  if (step.trigger.kind === 'follows') return step.label;
  return `${step.label}  ${TRIGGER_PREFIX[step.trigger.kind]} ${step.trigger.text}`;
}

const FLOW_LANE = Object.freeze({ name: 'Process flow', kind: 'context' as const });

export function toStepParsedFlow(flowText: string): ParsedFlow {
  const { steps, errors } = parseStepFlow(flowText);
  const statements: Statement[] = [];

  // Edge semantics follow the trigger: a `follows` step chains along the
  // trunk (the previous `follows` sibling, or the parent for the first); a
  // step with an explicit trigger (? on / at / by) BRANCHES from the point
  // the story had reached — the previous trunk step, or the parent when the
  // branch opens the process. Narration attaches conditions to where the
  // flow just was ("if the customer exists [after searching] …"), so two
  // outcomes render as a fork off that step, never as a false sequence and
  // never as forks off the process start (owner-verified capture,
  // 2026-08-25).
  const walk = (
    node: StepNode,
    parentId: number | null,
    previousTrunkId: number | null,
  ) => {
    const upstream = previousTrunkId ?? parentId;
    const triggeredBy: Trigger | null = upstream === null
      ? null
      : { kind: 'from', actionId: upstream };
    statements.push({
      id: node.id,
      type: 'action',
      actor: FLOW_LANE,
      name: cardName(node),
      target: null,
      via: null,
      emits: [],
      triggeredBy,
      guard: null,
      tags: node.steps.length > 0 ? { subprocess: true } : {},
      source: `[${node.id}] ${node.label}`,
    });
    let childTrunk: number | null = null;
    for (const child of node.steps) {
      walk(child, node.id, childTrunk);
      if (child.trigger.kind === 'follows') childTrunk = child.id;
    }
  };

  for (const root of steps) {
    walk(root, null, null);
  }

  return {
    statements,
    actors: statements.length > 0 ? [FLOW_LANE] : [],
    systems: [],
    targets: [],
    errors: errors.map((message) => ({
      statementId: null, severity: 'warning', code: 'step-flow', message, source: null,
    })),
  };
}
